import { applyDiscountRate, cents, percentToPpm, tryParseMoney } from '@sitzmark/core';

/**
 * The arithmetic behind the line-adjust dialog.
 *
 * Pure and separated from the component so it can be tested directly — the same
 * convention as `wizard-machine.ts` and `receiving-lines.ts`. Two of these
 * functions mirror decisions the server has already made, and they call the
 * *same* `@sitzmark/core` primitives to do it, rather than reimplementing them:
 *
 *  - `previewNet` uses `applyDiscountRate`, so the figure shown before the
 *    button is pressed is the figure that ends up on the receipt. A preview
 *    that rounds differently from the engine is worse than no preview.
 *  - `severityPpm` mirrors `discountSeverityPpm` in `routers/register.ts`, so
 *    the dialog can warn that a manager will be needed instead of discovering
 *    it from a rejection (§2.2-7).
 *
 * The server stays the authority in both cases. This is what lets the screen
 * tell the truth early, not a second opinion about it.
 */

export type AdjustMode = 'percent' | 'amount' | 'price';

export interface AdjustableLine {
  readonly id: string;
  readonly description: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
  readonly originalUnitPriceCents: number | null;
  readonly netCents: number;
  readonly lineDiscountCents: number;
  readonly priceOverridden: boolean;
  readonly note: string | null;
}

/** Digits and one decimal point, at most two places. Filter in, don't validate out. */
export function sanitiseAdjustment(raw: string): string {
  const digitsAndDot = raw.replace(/[^\d.]/g, '');
  const [whole = '', ...rest] = digitsAndDot.split('.');
  if (rest.length === 0) return whole;
  return `${whole}.${rest.join('').slice(0, 2)}`;
}

/** A percent stays a number; money becomes integer cents. Null when unusable. */
export function parseAdjustment(mode: AdjustMode, value: string): number | null {
  if (value.trim() === '') return null;
  if (mode === 'percent') {
    const percent = Number.parseFloat(value);
    return Number.isFinite(percent) ? percent : null;
  }
  return tryParseMoney(value);
}

export function grossOf(line: Pick<AdjustableLine, 'unitPriceCents' | 'quantity'>): number {
  return line.unitPriceCents * line.quantity;
}

/**
 * Why this adjustment cannot be applied, or undefined if it can.
 *
 * Feeds `disabledReason`, so an illegal entry disables the button *and says
 * why* rather than being error-dialogued after the press (§2.2-7).
 */
export function adjustmentProblem(
  mode: AdjustMode,
  parsed: number,
  line: Pick<AdjustableLine, 'unitPriceCents' | 'quantity'>,
): string | undefined {
  if (parsed < 0) return 'Enter a positive amount';
  if (mode === 'percent' && parsed > 100) return 'A discount cannot be more than 100%';
  if (mode === 'amount' && parsed > grossOf(line)) return 'That is more than the line is worth';
  return undefined;
}

/** What this line's net becomes — the number the customer will read. */
export function previewNet(
  mode: AdjustMode,
  parsed: number,
  line: Pick<AdjustableLine, 'unitPriceCents' | 'quantity'>,
): number {
  const gross = grossOf(line);
  if (mode === 'percent') return applyDiscountRate(cents(gross), percentToPpm(parsed));
  if (mode === 'amount') return Math.max(0, gross - parsed);
  return parsed * line.quantity;
}

/**
 * How steep this is, in parts per million, for comparison against the tenant's
 * manager-PIN threshold. Mirrors `discountSeverityPpm` on the server.
 */
export function severityPpm(
  mode: AdjustMode,
  parsed: number,
  line: Pick<AdjustableLine, 'unitPriceCents' | 'quantity'>,
): number {
  const gross = grossOf(line);
  if (mode === 'percent') return percentToPpm(parsed);
  if (mode === 'amount') {
    // A dollar discount on a zero-priced line is infinitely steep, which is the
    // server's reading too — it always wants a manager.
    return gross <= 0 ? Number.POSITIVE_INFINITY : Math.round((parsed / gross) * 1_000_000);
  }
  if (line.unitPriceCents <= 0) return Number.POSITIVE_INFINITY;
  // A price *increase* is not a giveaway, so it is never over threshold.
  return Math.max(
    0,
    Math.round(((line.unitPriceCents - parsed) / line.unitPriceCents) * 1_000_000),
  );
}
